// hooks/useVideoProtection.js
// Client-side video protection: blocks common download/capture attempts.
// Layers: right-click block, keyboard shortcut block, DevTools detection,
// screen-capture detection, extension detection, src obfuscation.
import { useEffect, useRef, useCallback } from 'react';

/**
 * @param {Object} opts
 * @param {React.RefObject} opts.containerRef - ref to the video container div
 * @param {Function}        opts.onPause      - call to pause the video
 * @param {Function}        opts.onResume     - call to resume the video
 * @param {boolean}         opts.active       - only enable when true (video is playing)
 */
const useVideoProtection = ({ containerRef, onPause, onResume, active = true }) => {
  const devtoolsOpen = useRef(false);
  const warningRef = useRef(null);

  // ── Warning overlay helper (for devtools / extensions) ──
  const showWarning = useCallback((show) => {
    const el = containerRef?.current;
    if (!el) return;
    if (show) {
      if (warningRef.current) return; // already showing
      const overlay = document.createElement('div');
      overlay.className = 'vp-devtools-warning';
      overlay.innerHTML = `
        <div class="vp-devtools-warning__inner">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <p>Video playback paused</p>
          <span>Unauthorized screen capture or video download tool detected.<br/>Please disable any screen recording or video download extensions to continue.</span>
        </div>
      `;
      el.style.position = el.style.position || 'relative';
      el.appendChild(overlay);
      warningRef.current = overlay;
    } else {
      if (warningRef.current) {
        warningRef.current.remove();
        warningRef.current = null;
      }
    }
  }, [containerRef]);

  // ── 1. Block right-click on video container ──
  useEffect(() => {
    if (!active) return;
    const el = containerRef?.current;
    if (!el) return;
    const handler = (e) => { e.preventDefault(); e.stopPropagation(); };
    el.addEventListener('contextmenu', handler, true);
    return () => el.removeEventListener('contextmenu', handler, true);
  }, [containerRef, active]);

  // ── 2. Block keyboard shortcuts (Ctrl+S, Ctrl+U, Ctrl+Shift+I, F12, PrintScreen) ──
  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      // Ctrl+S (save), Ctrl+U (view source)
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S' || e.key === 'u' || e.key === 'U')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // Ctrl+Shift+I (DevTools), Ctrl+Shift+J (Console), Ctrl+Shift+C (Inspector)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && ['i', 'I', 'j', 'J', 'c', 'C'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // F12
      if (e.key === 'F12') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        navigator.clipboard?.writeText?.('').catch(() => {});
        return;
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [active]);

  // ── 3. DevTools detection (window size heuristic) ──
  useEffect(() => {
    if (!active) return;
    let intervalId;

    const check = () => {
      const widthThreshold = window.outerWidth - window.innerWidth > 160;
      const heightThreshold = window.outerHeight - window.innerHeight > 160;
      const isOpen = widthThreshold || heightThreshold;

      if (isOpen && !devtoolsOpen.current) {
        devtoolsOpen.current = true;
        onPause?.();
        showWarning(true);
      } else if (!isOpen && devtoolsOpen.current) {
        devtoolsOpen.current = false;
        showWarning(false);
        onResume?.();
      }
    };

    // Check every 1.5 seconds — lightweight
    intervalId = setInterval(check, 1500);
    return () => clearInterval(intervalId);
  }, [active, onPause, onResume, showWarning]);

  // ── 4. Screen capture API interception — block getDisplayMedia ──
  useEffect(() => {
    if (!active || !navigator.mediaDevices) return;
    const origGetDisplayMedia = navigator.mediaDevices.getDisplayMedia;
    if (!origGetDisplayMedia) return;
    navigator.mediaDevices.getDisplayMedia = function () {
      onPause?.();
      showWarning(true);
      return Promise.reject(new DOMException('Screen capture blocked', 'NotAllowedError'));
    };
    return () => {
      navigator.mediaDevices.getDisplayMedia = origGetDisplayMedia;
    };
  }, [active, onPause, showWarning]);

  // ── 5. MediaRecorder interception — block JS-based recording tools/extensions ──
  useEffect(() => {
    if (!active) return;
    const OriginalMediaRecorder = window.MediaRecorder;
    if (!OriginalMediaRecorder) return;

    window.MediaRecorder = function (...args) {
      const stream = args[0];
      // Check if any track is a screen/video capture track
      const hasVideoTrack = stream?.getVideoTracks?.()?.some(
        (t) => t.kind === 'video'
      );
      if (hasVideoTrack) {
        // Stop all tracks in the stream to kill the recording
        stream.getTracks?.().forEach((t) => t.stop());
        onPause?.();
        showWarning(true);
        throw new DOMException('Recording is not allowed', 'NotAllowedError');
      }
      return new OriginalMediaRecorder(...args);
    };
    // Copy static methods & prototype
    window.MediaRecorder.prototype = OriginalMediaRecorder.prototype;
    window.MediaRecorder.isTypeSupported = OriginalMediaRecorder.isTypeSupported;

    return () => {
      window.MediaRecorder = OriginalMediaRecorder;
    };
  }, [active, onPause, showWarning]);

  // ── 6. (Removed — Permissions API display-capture query returns stale
  //    "granted" state from past grants, causing false positives.) ──

  // ── 7. Picture-in-Picture prevention ──
  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      // Prevent entering PiP mode (users use PiP + record the small window)
      e.preventDefault();
      onPause?.();
    };
    document.addEventListener('enterpictureinpicture', handler, true);
    return () => document.removeEventListener('enterpictureinpicture', handler, true);
  }, [active, onPause]);

  // ── 8. Clipboard wipe — clear screenshots from clipboard on copy events ──
  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      // If a copy event fires while our video container is visible, wipe image data
      const el = containerRef?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        // Overwrite clipboard with empty text to destroy any screenshot
        e.clipboardData?.setData?.('text/plain', '');
        e.preventDefault();
      }
    };
    document.addEventListener('copy', handler, true);
    return () => document.removeEventListener('copy', handler, true);
  }, [active, containerRef]);

  // ── 9. Video downloader extension detection (DOM MutationObserver) ──
  // Extensions inject download buttons, overlays, or icons near <video> elements.
  // We watch for suspicious injections and pause + warn when detected.
  useEffect(() => {
    if (!active) return;
    const el = containerRef?.current;
    if (!el) return;

    // Known CSS selectors injected by popular video downloader extensions
    // IMPORTANT: Keep these specific to avoid false positives with app UI elements
    const SUSPICIOUS_SELECTORS = [
      '[class*="savefrom"]', '[id*="savefrom"]',
      '[class*="video-download"]', '[class*="vdh-"]',
      '[class*="yt-dl"]', '[class*="grabber"]',
      '[class*="video-dl"]',
    ];

    // Check for injected elements once
    const scanForExtensions = () => {
      for (const sel of SUSPICIOUS_SELECTORS) {
        try {
          const found = el.querySelectorAll(sel);
          for (const node of found) {
            // Ignore our own elements
            if (node.closest('.content-watermark') || node.closest('.vp-devtools-warning') || node.closest('.vp-shield')) continue;
            // Suspicious element found — likely a downloader extension injection
            node.remove();
            onPause?.();
            showWarning(true);
            return true;
          }
        } catch { /* invalid selector in some browsers */ }
      }
      return false;
    };

    // Watch for new elements injected into the video container
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue; // only elements
          // Skip our own overlays
          if (node.classList?.contains('content-watermark') || node.classList?.contains('vp-devtools-warning') || node.classList?.contains('vp-shield')) continue;
          // Check if injected element looks like a download button
          const cls = (typeof node.className === 'string' ? node.className : node.className?.baseVal || '').toLowerCase();
          const id = (node.id || '').toLowerCase();
          if (
            cls.includes('savefrom') || cls.includes('video-dl') ||
            cls.includes('video-download') ||
            cls.includes('grabber') || cls.includes('vdh-') ||
            cls.includes('yt-dl') ||
            id.includes('savefrom') || id.includes('video-download')
          ) {
            node.remove();
            onPause?.();
            showWarning(true);
            return;
          }
        }
      }
    });

    observer.observe(el, { childList: true, subtree: true });
    // Also observe document body — some extensions inject at body level
    const bodyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          const cls = (typeof node.className === 'string' ? node.className : node.className?.baseVal || '').toLowerCase();
          const id = (node.id || '').toLowerCase();
          if (
            cls.includes('savefrom') || cls.includes('video-download') ||
            cls.includes('vdh-') || id.includes('savefrom') ||
            cls.includes('grabber') || cls.includes('video-dl') ||
            cls.includes('yt-dl')
          ) {
            node.remove();
            onPause?.();
            showWarning(true);
            return;
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: false });

    // Initial scan
    scanForExtensions();

    return () => {
      observer.disconnect();
      bodyObserver.disconnect();
    };
  }, [active, containerRef, onPause, showWarning]);

  // ── 10. Obfuscate video src — hide CDN URL from DOM inspection ──
  // After video starts loading, we remove the `src` attribute from the DOM.
  // The browser keeps streaming from its internal buffer. Extensions scanning
  // the DOM for <video src="..."> will find nothing.
  useEffect(() => {
    if (!active) return;
    const el = containerRef?.current;
    if (!el) return;

    const videos = el.querySelectorAll('video');
    const cleanups = [];

    videos.forEach((video) => {
      const handler = () => {
        // Video has enough data buffered — safe to hide the src from DOM
        if (video.readyState >= 2) { // HAVE_CURRENT_DATA or better
          const realSrc = video.getAttribute('src');
          if (realSrc && !realSrc.startsWith('blob:')) {
            // Remove src from DOM attribute (browser keeps internal reference)
            video.removeAttribute('src');
            // Store in a closure so React re-renders don't break
            video._protectedSrc = realSrc;
          }
        }
      };

      video.addEventListener('loadeddata', handler);
      cleanups.push(() => video.removeEventListener('loadeddata', handler));
    });

    return () => cleanups.forEach((fn) => fn());
  }, [active, containerRef]);

  // ── 11. Detect known extensions via chrome-extension:// resource probing ──
  useEffect(() => {
    if (!active) return;
    // Well-known video downloader extension IDs
    const KNOWN_EXTENSIONS = [
      { id: 'modheajhcemahkckbhlocagnfiahkhig', name: 'Video DownloadHelper' },
      { id: 'lmjnegcaeklhafolokijcfjliaokphfk', name: 'SaveFrom.net' },
      { id: 'dcfofgiipmnbfmkjcleoagandoghbmgc', name: 'FVD Video Downloader' },
      { id: 'bgnkhhnnamicmpeenaelnjfhikgbkllg', name: 'Video download helper' },
      { id: 'oofgbpoabipfcfjapgnbbjjaenockbdp', name: 'Video Downloader Pro' },
      { id: 'hnmpcagpplmpfistfmekhfnljnkjbfgj', name: 'Video Downloader Plus' },
      { id: 'pgifbheondldjicnhpcemedgpkakaldm', name: 'Video Downloader' },
      { id: 'cjpalhdlnbpafiamejdnhcphjbkeiagm', name: 'uBlock Origin' }, // not a downloader, skip false positive
    ];

    // Remove uBlock (not a downloader)
    const downloaders = KNOWN_EXTENSIONS.filter(e => e.name !== 'uBlock Origin');

    let detected = false;

    downloaders.forEach(({ id }) => {
      // Try to load the extension's manifest icon — if it loads, extension is installed
      const img = new Image();
      img.onload = () => {
        if (!detected) {
          detected = true;
          onPause?.();
          showWarning(true);
        }
      };
      img.onerror = () => {};
      // Chrome extensions expose certain resources at this URL pattern
      img.src = `chrome-extension://${id}/icon.png`;
      // Fallback common paths
      setTimeout(() => {
        if (detected) return;
        const img2 = new Image();
        img2.onload = () => {
          if (!detected) {
            detected = true;
            onPause?.();
            showWarning(true);
          }
        };
        img2.onerror = () => {};
        img2.src = `chrome-extension://${id}/icons/icon16.png`;
      }, 500);
    });
  }, [active, onPause, showWarning]);

  // ── 12. Virtual screen-capture device detection ──
  // Screen recorders (OBS, Bandicam) often install virtual audio/video devices.
  // Detect them via enumerateDevices and warn.
  useEffect(() => {
    if (!active || !navigator.mediaDevices?.enumerateDevices) return;

    const SUSPICIOUS_DEVICE_NAMES = [
      'obs', 'virtual', 'screen capture', 'bandicam', 'camtasia',
      'xsplit', 'streamlabs', 'game capture', 'display capture',
      'manycam', 'snap camera', 'droidcam', 'nvidia broadcast',
    ];

    const check = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const suspicious = devices.find((d) => {
          const label = (d.label || '').toLowerCase();
          return SUSPICIOUS_DEVICE_NAMES.some((name) => label.includes(name));
        });
        if (suspicious) {
          onPause?.();
          showWarning(true);
        }
      } catch { /* some browsers restrict enumerateDevices */ }
    };

    check();
    // Re-check when devices change (user starts OBS mid-session)
    const handler = () => check();
    navigator.mediaDevices.addEventListener('devicechange', handler);
    return () => navigator.mediaDevices.removeEventListener('devicechange', handler);
  }, [active, onPause, showWarning]);

  // ── 13. Frame-rate drop detection (potential screen recording indicator) ──
  // Screen recording tools can cause frame drops. If we detect consistently
  // low frame rate, warn the user (soft detection — not definitive).
  useEffect(() => {
    if (!active) return;
    let rafId;
    let lastTime = 0;
    let lowFrameCount = 0;
    const LOW_FPS_THRESHOLD = 15; // frames below this are suspicious
    const CONSECUTIVE_ALERTS = 10; // need 10 consecutive low frames

    const checkFrame = (timestamp) => {
      if (lastTime) {
        const delta = timestamp - lastTime;
        const fps = 1000 / delta;
        if (fps < LOW_FPS_THRESHOLD && fps > 1) {
          lowFrameCount++;
          if (lowFrameCount >= CONSECUTIVE_ALERTS) {
            // Reset counter so we don't spam
            lowFrameCount = 0;
            // Don't auto-pause — just soft-warn. Low FPS can have legitimate causes.
          }
        } else {
          lowFrameCount = 0;
        }
      }
      lastTime = timestamp;
      rafId = requestAnimationFrame(checkFrame);
    };

    rafId = requestAnimationFrame(checkFrame);
    return () => cancelAnimationFrame(rafId);
  }, [active]);

  // Cleanup overlays on unmount
  useEffect(() => {
    return () => {
      if (warningRef.current) {
        warningRef.current.remove();
        warningRef.current = null;
      }
    };
  }, []);
};

export default useVideoProtection;
